import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Empty, ErrorMsg, Loading, Modal, Section, Time } from '../../components/ui';
import { api, errorText, newIdempotencyKey } from '../../lib/api';
import type { Asset, MirageVideoProject, VideoFormat, VideoLink, VideoMeta, VideoOptions, VideoStatus } from '../../lib/contentTypes';
import { CopyButton } from './Publications';
import { MusicUpload } from './MotionPreview';

// Videos are made in Mirage Video (docs/PLAN-mirage-video-integration.md): this app sends the approved
// images and a brief, Mirage Video builds and renders the film, and finished films come back into the
// publication's files. Only owners make or open a video; everyone sees where it stands.

export const VIDEO_STATUS_LABEL: Record<VideoStatus, string> = {
  frames: 'waiting for its slides',
  queued: 'waiting for the worker',
  creating: 'being created in Mirage Video',
  failed: 'failed',
  draft: 'storyboard draft',
  mockup: 'mockup rendered',
  approved: 'approved in Mirage Video',
  film: 'film rendered',
  gone: 'deleted in Mirage Video',
};
const TONE: Record<VideoStatus, string> = {
  frames: 'idle',
  queued: 'idle',
  creating: 'warn',
  failed: 'bad',
  draft: 'warn',
  mockup: 'warn',
  approved: 'ok',
  film: 'live',
  gone: 'bad',
};
const FORMAT_LABEL: Record<VideoFormat, string> = {
  '9:16': '9:16 short / reel / story',
  '4:5': '4:5 feed post',
  '1:1': '1:1 square',
  '16:9': '16:9 film',
};
const busy = (v: VideoLink) => v.status === 'queued' || v.status === 'creating';

function originText(v: VideoLink): string {
  if (v.origin === 'linked') return `Linked from Mirage Video: ${v.mv_project_name ?? v.mv_project_id ?? 'project'}`;
  if (v.origin === 'motion') return `Motion version of a Content Studio item${v.revision ? ` (revision ${v.revision})` : ''}`;
  return '';
}

export function VideoStatusBadge({ status }: { status: VideoStatus }) {
  return (
    <span className={`cs-status tone-${TONE[status]}`} data-video-status={status}>
      <span className={`sq ${status === 'queued' ? 'outline' : 'filled'} tone-${TONE[status]}`} aria-hidden="true" /> {VIDEO_STATUS_LABEL[status]}
    </span>
  );
}

export function useVideoMeta() {
  const [meta, setMeta] = useState<VideoMeta | null>(null);
  useEffect(() => {
    api<VideoMeta>('/api/content/videos/meta')
      .then(setMeta)
      .catch(() => setMeta(null));
  }, []);
  return meta;
}

function useVideos(query: string) {
  const [videos, setVideos] = useState<VideoLink[] | null>(null);
  const [error, setError] = useState('');
  const load = useCallback(
    () =>
      api<{ videos: VideoLink[] }>(`/api/content/videos?${query}`)
        .then((d) => {
          setVideos(d.videos);
          setError('');
          return d.videos;
        })
        .catch((e) => {
          setError(errorText(e));
          return null;
        }),
    [query],
  );
  useEffect(() => {
    void load();
  }, [load]);
  return { videos, error, load };
}

/** The Videos section of a publication page. `onFilms` runs when a new film has been copied in. */
export function PublicationVideos({
  publicationId,
  projectId,
  projectTitle,
  projectLocation,
  onFilms,
}: {
  publicationId: string;
  projectId: string;
  projectTitle: string;
  projectLocation: string | null;
  onFilms: () => void;
}) {
  const meta = useVideoMeta();
  const { videos, error, load } = useVideos(`publication_id=${publicationId}`);
  const [open, setOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [watchUntil, setWatchUntil] = useState(0);
  const films = useRef<number | null>(null);

  const total = videos?.reduce((n, v) => n + v.films.length, 0) ?? null;
  useEffect(() => {
    if (total === null) return;
    if (films.current !== null && total > films.current) onFilms();
    films.current = total;
  }, [total, onFilms]);

  const watching = (videos?.some(busy) ?? false) || watchUntil > Date.now();
  useEffect(() => {
    if (!watching) return;
    const t = setInterval(() => {
      void load();
      if (watchUntil && watchUntil <= Date.now()) setWatchUntil(0);
    }, 3000);
    return () => clearInterval(t);
  }, [watching, watchUntil, load]);

  async function check(v: VideoLink) {
    setErr('');
    setMsg('');
    try {
      await api(`/api/content/videos/${v.id}/sync`, { method: 'POST' });
      setMsg('Checking Mirage Video…');
      setWatchUntil(Date.now() + 30_000);
    } catch (e) {
      setErr(errorText(e));
    }
  }
  async function unlink(v: VideoLink) {
    const what = v.origin === 'linked' ? 'Remove this link? The films already copied stay in the files; nothing changes in Mirage Video.' : 'Remove this unstarted motion version?';
    if (!window.confirm(what)) return;
    setErr('');
    try {
      await api(`/api/content/videos/${v.id}`, { method: 'DELETE' });
      setMsg('Removed.');
      void load();
    } catch (e) {
      setErr(errorText(e));
    }
  }
  async function openInMirageVideo(v: VideoLink) {
    setErr('');
    // Opened inside the click so the browser does not block it; pointed at the sign-in link once known.
    const w = window.open('', '_blank');
    try {
      const { url } = await api<{ url: string }>(`/api/content/videos/${v.id}/open`, { method: 'POST' });
      if (w) {
        w.opener = null;
        w.location.href = url;
      } else window.location.href = url;
    } catch (e) {
      w?.close();
      setErr(errorText(e));
    }
  }

  const canMake = !!meta?.configured && !!meta?.can_make;
  return (
    <Section
      title={`Videos${videos ? ` (${videos.length})` : ''}`}
      id="videos"
      aside={
        canMake && (
          <span className="row gap-s wrap">
            <button type="button" className="btn small" onClick={() => setLinking(true)}>
              Link an existing project
            </button>
            <button type="button" className="btn small primary" onClick={() => setOpen(true)}>
              Make the video
            </button>
          </span>
        )
      }
    >
      <p className="muted small">
        Made in Mirage Video from this project's approved images (or linked to a project already made there): a draft storyboard is built there, you refine, approve and render it in Mirage Video, and the finished film comes back into the files above by itself (checked every 5 minutes).
      </p>
      {meta && !meta.configured && <p className="msg warn">{meta.reason}</p>}
      {meta?.configured && !meta.can_make && <p className="muted small">Only an owner can make a video or open Mirage Video from here.</p>}
      {msg && (
        <p className="small" role="status">
          {msg}
        </p>
      )}
      <ErrorMsg>{err || error}</ErrorMsg>
      {!videos ? (
        <Loading what="Loading videos" />
      ) : videos.length === 0 ? (
        <Empty>No video yet.</Empty>
      ) : (
        <div className="stack">
          {videos.map((v) => (
            <article key={v.id} className="cs-video" data-video={v.id} data-origin={v.origin} aria-label={`Video ${v.format}`}>
              <header className="row gap center wrap">
                <VideoStatusBadge status={v.status} />
                <span className="small">{FORMAT_LABEL[v.format]}</span>
                <span className="muted small num">
                  {v.origin === 'linked' ? `${v.storyboard?.item_count ?? 0} block${v.storyboard?.item_count === 1 ? '' : 's'}` : `${v.shots} ${v.origin === 'motion' ? 'slide' : 'image'}${v.shots === 1 ? '' : 's'}`}
                  {v.storyboard?.duration_sec ? ` · ${v.storyboard.duration_sec.toFixed(1)} s` : ''}
                  {v.renders.mockups ? ` · ${v.renders.mockups} mockup${v.renders.mockups === 1 ? '' : 's'}` : ''}
                </span>
                <span className="muted small">
                  {v.origin === 'linked' ? 'linked' : 'made'} <Time iso={v.created_at} relative />
                  {v.last_synced_at && (
                    <>
                      {' '}
                      · checked <Time iso={v.last_synced_at} relative />
                    </>
                  )}
                </span>
              </header>
              {v.error && (
                <p className={`msg ${v.status === 'failed' || v.status === 'gone' ? 'err' : 'warn'}`} role={v.status === 'failed' ? 'alert' : undefined}>
                  {v.error}
                </p>
              )}
              {v.origin !== 'made' && <p className="small">{originText(v)}</p>}
              {v.status === 'frames' && v.frames && (
                <p className="small muted">
                  {v.frames.received} of {v.frames.expected} slides received: the motion version starts once every slide is sent (from the item's page).
                </p>
              )}
              {v.skipped_films > 0 && (
                <p className="small muted">
                  {v.skipped_films} older film{v.skipped_films === 1 ? ' of this project stays' : 's of this project stay'} in Mirage Video; only the latest was copied, and every new one will be.
                </p>
              )}
              {v.style_notes.length > 0 && <p className="small warn-text">{v.style_notes.join(' · ')}</p>}
              {v.status === 'draft' && v.origin !== 'linked' && <p className="small">The storyboard is ready to refine in Mirage Video: moves, texts, music, then a mockup for approval.</p>}
              {v.films.length > 0 && (
                <ul className="plain stack">
                  {v.films.map((f) => (
                    <li key={f.render_id} className="small" data-film={f.render_id}>
                      Film {f.width}×{f.height}
                      {f.duration_sec ? `, ${f.duration_sec.toFixed(1)} s` : ''}, {(f.bytes / 1_000_000).toFixed(1)} MB: in the files above
                      {f.url ? (
                        <>
                          {' '}
                          (
                          <a href={f.url} target="_blank" rel="noreferrer noopener">
                            play
                          </a>
                          )
                        </>
                      ) : (
                        ' (the file was removed from this publication)'
                      )}
                      <span className="muted"> · SHA-256 {f.sha256.slice(0, 12)}…</span>
                      {f.credits.length > 0 && (
                        <div className="row gap center wrap">
                          <span className="warn-text">Music credit to add to each text: {f.credits.join(' · ')}</span>
                          <CopyButton value={f.credits.join('\n')} label="Copy credit" />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <div className="row gap center wrap">
                {canMake && v.mv_project_id && v.status !== 'gone' && (
                  <button type="button" className="btn small" onClick={() => void openInMirageVideo(v)}>
                    Open in Mirage Video
                  </button>
                )}
                {meta?.configured && v.mv_project_id && (
                  <button type="button" className="btn ghost small" onClick={() => void check(v)}>
                    Check now
                  </button>
                )}
                {canMake && (v.origin === 'linked' || (v.origin === 'motion' && v.status === 'frames')) && (
                  <button type="button" className="btn ghost small" onClick={() => void unlink(v)}>
                    {v.origin === 'linked' ? 'Remove link' : 'Remove'}
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
      {linking && (
        <LinkProjectDialog
          publicationId={publicationId}
          onClose={() => setLinking(false)}
          onLinked={(v) => {
            setLinking(false);
            setMsg(v.films.length || v.skipped_films || v.renders.films ? 'Linked. Its latest film is being copied into the files.' : 'Linked. Films rendered from now on come back into the files.');
            setWatchUntil(Date.now() + 30_000);
            void load();
          }}
        />
      )}
      {open && (
        <MakeVideoDialog
          publicationId={publicationId}
          projectId={projectId}
          projectTitle={projectTitle}
          projectLocation={projectLocation}
          onClose={() => setOpen(false)}
          onCreated={() => {
            setOpen(false);
            setMsg('The video is being created in Mirage Video.');
            void load();
          }}
        />
      )}
    </Section>
  );
}

/** The project page lists its videos; they are made from a publication. */
export function ProjectVideos({ projectId }: { projectId: string }) {
  const meta = useVideoMeta();
  const { videos, error } = useVideos(`project_id=${projectId}`);
  if (!videos?.length && !meta?.configured) return null;
  return (
    <Section title={`Videos${videos ? ` (${videos.length})` : ''}`} id="videos">
      <ErrorMsg>{error}</ErrorMsg>
      {!videos ? (
        <Loading what="Loading videos" />
      ) : videos.length === 0 ? (
        <Empty>No video yet. Open a publication below and choose “Make the video”.</Empty>
      ) : (
        <ul className="plain cs-projects">
          {videos.map((v) => (
            <li key={v.id} className="row gap center wrap" data-video={v.id}>
              <Link to={`/content/publications/${v.publication_id}#videos`}>{v.publication_title ?? 'publication'}</Link>
              <span className="muted small">{v.format}</span>
              <VideoStatusBadge status={v.status} />
              {v.films.length > 0 && (
                <span className="small">
                  {v.films.length} film{v.films.length === 1 ? '' : 's'} in its files
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

interface Shot {
  asset_id: string;
  movement: string;
  duration: string; // as typed
  fx: number;
  fy: number;
}

function MakeVideoDialog({
  publicationId,
  projectId,
  projectTitle,
  projectLocation,
  onClose,
  onCreated,
}: {
  publicationId: string;
  projectId: string;
  projectTitle: string;
  projectLocation: string | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [opts, setOpts] = useState<VideoOptions | null>(null);
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const [format, setFormat] = useState<VideoFormat>('9:16');
  const [shots, setShots] = useState<Shot[]>([]);
  const [f, setF] = useState({ intro: projectTitle, introSub: projectLocation ?? '', outro: '', outroSub: '', music: '', logo: '', transition: 'CROSSFADE' });

  useEffect(() => {
    Promise.all([
      api<VideoOptions>('/api/content/videos/options'),
      api<{ items: Asset[] }>(`/api/content/assets?project_id=${projectId}`),
    ])
      .then(([o, a]) => {
        const images = a.items.filter((x) => x.kind === 'image');
        setOpts(o);
        setAssets(images);
        setFormat(o.defaults.format);
        setF((x) => ({ ...x, outro: o.brand_name.split('(')[0].trim(), logo: o.default_logo_id ?? '', transition: o.defaults.transition }));
        setShots(
          images
            .filter((x) => x.usage_status === 'approved')
            .slice(0, 40)
            .map((x) => ({ asset_id: x.id, movement: o.defaults.movement, duration: String(o.defaults.duration_sec), fx: 0.5, fy: 0.5 })),
        );
      })
      .catch((e) => setError(errorText(e)));
  }, [projectId]);

  const approved = assets?.filter((a) => a.usage_status === 'approved') ?? [];
  const unapproved = (assets?.length ?? 0) - approved.length;
  const byId = new Map(approved.map((a) => [a.id, a]));
  const unused = approved.filter((a) => !shots.some((s) => s.asset_id === a.id));
  const seconds = shots.reduce((n, s) => n + (Number(s.duration) || 0), 0);
  const badDuration = shots.some((s) => !(Number(s.duration) >= 1 && Number(s.duration) <= 20));

  const setShot = (i: number, patch: Partial<Shot>) => setShots((all) => all.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i: number, d: number) =>
    setShots((all) => {
      const j = i + d;
      if (j < 0 || j >= all.length) return all;
      const next = [...all];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!opts) return;
    setSending(true);
    setError('');
    try {
      await api(`/api/content/publications/${publicationId}/videos`, {
        method: 'POST',
        idempotencyKey: key,
        body: {
          format,
          shots: shots.map((s) => ({
            asset_id: s.asset_id,
            movement: s.movement,
            intensity: opts.defaults.intensity,
            pace: opts.defaults.pace,
            duration_sec: Number(s.duration),
            focus_x: s.fx,
            focus_y: s.fy,
          })),
          transition: f.transition,
          intro: f.intro.trim() ? { title: f.intro.trim(), subtitle: f.introSub.trim() || null } : null,
          outro: f.outro.trim() ? { title: f.outro.trim(), subtitle: f.outroSub.trim() || null } : null,
          music_library_id: f.music || null,
          logo_asset_id: f.logo || null,
        },
      });
      onCreated();
    } catch (err) {
      setError(errorText(err));
      setSending(false);
    }
  }

  return (
    <Modal
      title="Make the video in Mirage Video"
      onClose={onClose}
      wide
      footer={
        <button type="submit" form="make-video" className="btn primary" disabled={!opts || sending || shots.length === 0 || badDuration}>
          {sending ? 'Sending…' : `Create the draft (${shots.length} image${shots.length === 1 ? '' : 's'}, ${seconds.toFixed(1)} s)`}
        </button>
      }
    >
      {!opts || !assets ? (
        error ? <ErrorMsg>{error}</ErrorMsg> : <Loading what="Asking Mirage Video for its options" />
      ) : (
        <form id="make-video" className="stack" onSubmit={submit} aria-label="Make the video">
          <fieldset className="field">
            <legend>Format</legend>
            <div className="row gap wrap">
              {opts.formats.map((x) => (
                <label key={x} className="check">
                  <input type="radio" name="format" value={x} checked={format === x} onChange={() => setFormat(x)} /> {FORMAT_LABEL[x]}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="field">
            <legend>Images, in film order ({shots.length})</legend>
            <p className="muted small">
              Only images approved for use are offered{unapproved > 0 ? ` (${unapproved} of this project's images are not approved)` : ''}; their originals are sent. Click a picture to set where the camera moves towards.
            </p>
            {approved.length === 0 && <p className="msg warn">This project has no image approved for use yet. Approve images on the project page first.</p>}
            <ol className="plain cs-shots">
              {shots.map((s, i) => {
                const a = byId.get(s.asset_id);
                return (
                  <li key={s.asset_id} className="cs-shot" data-shot={i}>
                    <button
                      type="button"
                      className="cs-shot-thumb"
                      aria-label={`Focus point of ${a?.filename ?? 'image'}`}
                      onClick={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        setShot(i, { fx: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), fy: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) });
                      }}
                    >
                      {a && <img src={a.urls.thumb} alt="" />}
                      <span className="cs-focus" style={{ left: `${s.fx * 100}%`, top: `${s.fy * 100}%` }} aria-hidden="true" />
                    </button>
                    <span className="cs-asset-name small" title={a?.filename}>
                      {i + 1}. {a?.filename}
                    </span>
                    <label className="field">
                      <span>Camera move</span>
                      <select value={s.movement} onChange={(e) => setShot(i, { movement: e.target.value })} aria-label={`Camera move ${i + 1}`}>
                        {opts.movements.map((m) => (
                          <option key={m.key} value={m.key}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span>Seconds</span>
                      <input type="number" min={1} max={20} step={0.5} value={s.duration} onChange={(e) => setShot(i, { duration: e.target.value })} aria-label={`Seconds ${i + 1}`} />
                    </label>
                    <span className="row gap">
                      <button type="button" className="btn ghost small" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move image ${i + 1} earlier`}>
                        ↑
                      </button>
                      <button type="button" className="btn ghost small" onClick={() => move(i, 1)} disabled={i === shots.length - 1} aria-label={`Move image ${i + 1} later`}>
                        ↓
                      </button>
                      <button type="button" className="btn ghost small" onClick={() => setShots(shots.filter((_, j) => j !== i))} aria-label={`Leave out image ${i + 1}`}>
                        Leave out
                      </button>
                    </span>
                  </li>
                );
              })}
            </ol>
            {unused.length > 0 && shots.length < 40 && (
              <label className="field cs-inline">
                <span>Add an image</span>
                <select
                  value=""
                  onChange={(e) => {
                    const id = e.target.value;
                    if (id) setShots([...shots, { asset_id: id, movement: opts.defaults.movement, duration: String(opts.defaults.duration_sec), fx: 0.5, fy: 0.5 }]);
                  }}
                  aria-label="Add an image"
                >
                  <option value="">choose…</option>
                  {unused.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.filename}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {badDuration && <p className="msg warn">Each image lasts between 1 and 20 seconds.</p>}
          </fieldset>

          <div className="form-grid">
            <label className="field">
              <span>Opening title (empty: none)</span>
              <input value={f.intro} maxLength={120} onChange={(e) => setF({ ...f, intro: e.target.value })} />
            </label>
            <label className="field">
              <span>Opening subtitle</span>
              <input value={f.introSub} maxLength={160} onChange={(e) => setF({ ...f, introSub: e.target.value })} />
            </label>
            <label className="field">
              <span>Closing title (empty: none)</span>
              <input value={f.outro} maxLength={120} onChange={(e) => setF({ ...f, outro: e.target.value })} />
            </label>
            <label className="field">
              <span>Closing subtitle</span>
              <input value={f.outroSub} maxLength={160} onChange={(e) => setF({ ...f, outroSub: e.target.value })} />
            </label>
            <label className="field">
              <span>Music (from Mirage Video's library)</span>
              <select value={f.music} onChange={(e) => setF({ ...f, music: e.target.value })}>
                <option value="">no music</option>
                {opts.music.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label || m.original_name}
                    {m.duration_sec ? ` (${Math.round(m.duration_sec)} s)` : ''}
                    {m.credit ? ' · credit required' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Logo (brand kit)</span>
              <select value={f.logo} onChange={(e) => setF({ ...f, logo: e.target.value })}>
                <option value="">no logo</option>
                {opts.logos.map((l) => (
                  <option key={l.asset_id} value={l.asset_id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Between images</span>
              <select value={f.transition} onChange={(e) => setF({ ...f, transition: e.target.value })}>
                {opts.transitions.map((t) => (
                  <option key={t} value={t}>
                    {t.replace('_', ' ').toLowerCase()}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <MusicUpload
            onUploaded={(t) => {
              setOpts((o) => (o ? { ...o, music: o.music.some((m) => m.id === t.id) ? o.music : [...o.music, t] } : o));
              setF((x) => ({ ...x, music: t.id }));
            }}
          />
          {opts.style_notes.length > 0 && <p className="small warn-text">{opts.style_notes.join(' · ')}</p>}
          <p className="muted small">
            Sent to Mirage Video: the brand's fonts, colours and tone of voice, the images above (originals), the titles you see here, the project's public title, location and description. Never the client's name or internal notes. Nothing is published: the draft waits for you in Mirage Video.
          </p>
          <ErrorMsg>{error}</ErrorMsg>
        </form>
      )}
    </Modal>
  );
}

/** Search Mirage Video's projects and link one to this publication (read only from here on). */
function LinkProjectDialog({ publicationId, onClose, onLinked }: { publicationId: string; onClose: () => void; onLinked: (v: VideoLink) => void }) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState<MirageVideoProject[] | null>(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      api<{ projects: MirageVideoProject[] }>(`/api/content/videos/projects?q=${encodeURIComponent(q.trim())}`)
        .then((d) => {
          setFound(d.projects);
          setError('');
        })
        .catch((e) => setError(errorText(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  async function link(p: MirageVideoProject) {
    setSending(p.id);
    setError('');
    try {
      const { video } = await api<{ video: VideoLink }>(`/api/content/publications/${publicationId}/videos/link`, { method: 'POST', body: { mv_project_id: p.id } });
      onLinked(video);
    } catch (e) {
      setError(errorText(e));
      setSending('');
    }
  }

  return (
    <Modal title="Link an existing Mirage Video project" onClose={onClose} wide>
      <div className="stack">
        <p className="muted small">
          A linked project is only read from here: its status, its finished films (the latest one now, every new one later) and “Open in Mirage Video”. Nothing is sent to it and its storyboard is never changed.
        </p>
        <label className="field">
          <span>Search by name or location</span>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Lozenets" aria-label="Search Mirage Video projects" autoFocus />
        </label>
        <ErrorMsg>{error}</ErrorMsg>
        {!found ? (
          error ? null : <Loading what="Asking Mirage Video for its projects" />
        ) : found.length === 0 ? (
          <Empty>No project matches.</Empty>
        ) : (
          <ul className="plain stack cs-mv-projects">
            {found.map((p) => (
              <li key={p.id} className="row gap center wrap" data-mv-project={p.id}>
                <strong>{p.name}</strong>
                {p.location && <span className="muted small">{p.location}</span>}
                <span className="muted small num">
                  {p.format ?? ''} · {p.final_renders} film{p.final_renders === 1 ? '' : 's'}
                  {p.updated_at && (
                    <>
                      {' '}
                      · changed <Time iso={p.updated_at} relative />
                    </>
                  )}
                </span>
                {p.made_here && <span className="small muted">made from Mirage Marketing</span>}
                {p.links_here > 0 && <span className="small muted">linked here {p.links_here}×</span>}
                <button type="button" className="btn small" disabled={!!sending} onClick={() => void link(p)} aria-label={`Link ${p.name}`}>
                  {sending === p.id ? 'Linking…' : 'Link'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
